'use client'

import { useCartId } from '@/feature/cart/hooks/useCartId'
import { useAbTastyCampaignActive } from '@/feature/experimentation/hooks/useAbTastyCampaignActive.client'
import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { useNextIsClientHydrated } from '@/lib/next/useNextIsClientHydrated'
import { useIsReactNativeWebView } from '@/lib/react-native/contexts/react-native/ReactNativeProvider.client'

// AbTasty campaign "CATA2" targeting: loggedin users on prolians
const QUICK_ADD_TO_CART_AB_TASTY_CAMPAIGN_ID = 1587524

type UseProductCardHoverParams = {
  isArticle: boolean
  canAddToCart: boolean
  noQuickAddToCart?: boolean
  hasExternalHover?: boolean
}

// Derives whether the hover feature (stroke + quick add-to-cart CTA) applies to this product card.
// The hover mechanics themselves (group/data-hovered) live in ProductCardHoverGroup
export function useProductCardHover({
  isArticle,
  canAddToCart,
  noQuickAddToCart,
  hasExternalHover,
}: UseProductCardHoverParams) {
  const isHydrated = useNextIsClientHydrated()
  const isAuthenticated = useIsAuthenticated()
  const isInWebView = useIsReactNativeWebView()
  const cartId = useCartId()
  const isAbTastyCampaignActive = useAbTastyCampaignActive(QUICK_ADD_TO_CART_AB_TASTY_CAMPAIGN_ID)

  const isQuickAddToCartActivated = isAbTastyCampaignActive && !noQuickAddToCart
  // This card owns its hover group unless an ancestor already provides one (for example the bundle overlay)
  const managesOwnHover = isQuickAddToCartActivated && !hasExternalHover
  // CTA + stock fade only for addable articles on non-touch/non-webview, deferred to client (isHydrated) to avoid a hydration mismatch
  // isAuthenticated is a pre-existing cart business rule, not AbTasty plumbing; it just happens to also mirror the campaign's own "visiteurs connectés" targeting
  const showAddToCart =
    isQuickAddToCartActivated &&
    isArticle &&
    isAuthenticated &&
    isHydrated &&
    !isInWebView &&
    !!cartId &&
    !!canAddToCart

  return { isQuickAddToCartActivated, managesOwnHover, showAddToCart }
}
